import React, { FC, memo, useMemo } from "react";
import { AttributeType, StringSubType } from "@evergis/api";
import { Dropdown, IconButton, IconToggle, Input, Tooltip } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { AttributeError } from "../../../../../utils/validateAttributeName";

import { Container, Flex } from "../Layer/styled";
import { Description, TableAttributeActions, TooltipContent, TooltipLabel } from "./styled";

import { fieldTypes, getFieldTypeLangKey, getFieldTypeValue } from "../../../../../evergis/core/attributes/fieldTypes";
import { useTableAttributeUpdate } from "../../hooks/useTableAttributeUpdate";
import { useValueChanged } from "../../hooks/useValueChanged";
import { useAttributesError } from "../../../../../hooks/useAttributesError";

import { AttributesUpdate } from "../../../../../hooks/attributes/useAttributes";

export interface TableAttribute {
  name: string;
  type: AttributeType;
  subType?: StringSubType;
  id?: string;
  editable?: boolean;
  hasIndex?: boolean;
}

type TableAttributeUpdate = Pick<AttributesUpdate<TableAttribute>, "deleteAttribute" | "updateAttribute">;

type TableAttributeProps = {
  attribute: TableAttribute;
  idField?: string;
  error?: AttributeError;
  isEditType?: boolean;
} & TableAttributeUpdate;

export const TableAttributeItem: FC<TableAttributeProps> = memo(
  ({ attribute, idField, error, isEditType, deleteAttribute, updateAttribute }) => {
    const { t } = useTranslation("dialogs");
    const { help, error: attributesError } = useAttributesError(error);
    const { name = "", type, editable, id, subType, hasIndex } = attribute;
    const isNameChanged = useValueChanged(name);

    const processedFieldTypes = fieldTypes.filter(
      value => ![AttributeType.Point, AttributeType.MultiPolygon].includes(value.value as AttributeType),
    );

    const { updateName, updateType, toggleIndex, deleteTableAttribute } = useTableAttributeUpdate(
      id,
      updateAttribute,
      deleteAttribute,
    );

    const indexesTooltip = useMemo(
      () => (
        <div>
          <TooltipLabel>{t("editResource.attributeIndex")}</TooltipLabel>
          <TooltipContent>{t("editResource.attributeIndexDescription")}</TooltipContent>
        </div>
      ),
      [t],
    );

    return editable ? (
      <Container style={{ alignItems: "flex-start", padding: "0.75rem 1 rem" }}>
        <Flex>
          <Input
            type="text"
            value={name}
            width={"23rem"}
            label={t("editResource.attributeName", { ns: "dialogs" })}
            placeholder={t("editResource.attributeNameLat", { ns: "dialogs" })}
            // eslint-disable-next-line
            help={isNameChanged && error ? attributesError : help}
            onChange={updateName}
            error={isNameChanged && !!error}
          />
        </Flex>
        <Flex style={{ alignItems: "flex-end" }}>
          <Dropdown
            value={getFieldTypeValue(type, subType)}
            width={"22rem"}
            label={t("editResource.attributeType", { ns: "dialogs" })}
            options={processedFieldTypes.map(value => ({
              ...value,
              text: value.langKey ? t(value.langKey, { ns: "common" }) : value.text,
            }))}
            onChange={updateType}
            zIndex={120}
          />
          <TableAttributeActions>
            <Tooltip arrow maxWidth={"11.25rem"} placement={"top"} content={indexesTooltip} delay={0.5}>
              {ref => (
                <IconToggle
                  innerRef={ref}
                  kind={"refresh_circle"}
                  isSelected={hasIndex}
                  disabled={name === idField}
                  onClick={() => toggleIndex(!hasIndex)}
                />
              )}
            </Tooltip>
            <IconButton
              kind={"delete"}
              title={t("editResource.deleteAttribute", { ns: "dialogs" })}
              disabled={name === idField}
              onClick={deleteTableAttribute}
            />
          </TableAttributeActions>
        </Flex>
      </Container>
    ) : (
      <Container key={`attr_${name}`}>
        <Flex style={{ flexDirection: "column", alignItems: "flex-start" }}>
          <div>{name}</div>
          <Description>{t(getFieldTypeLangKey(type), { ns: "common" })}</Description>
        </Flex>
        {isEditType && (
          <TableAttributeActions>
            <Tooltip arrow maxWidth={"11.25rem"} placement={"top"} content={indexesTooltip} delay={0.5}>
              {ref => (
                <IconToggle
                  innerRef={ref}
                  kind={"refresh_circle"}
                  isSelected={hasIndex}
                  disabled={name === idField}
                  onClick={() => toggleIndex(!hasIndex)}
                />
              )}
            </Tooltip>
            <IconButton
              kind={"delete"}
              title={t("editResource.deleteAttribute", { ns: "dialogs" })}
              disabled={name === idField}
              onClick={deleteTableAttribute}
            />
          </TableAttributeActions>
        )}
      </Container>
    );
  },
);
